import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWhatsAppLink } from "@/hooks/useWhatsAppLink";
import {
  FALLBACK_WA_SOFT_ASK,
  decideWaSoftAsk,
  isWaSoftAskDecisionFinal,
  parseWaSoftAskState,
  waSoftAskDayKey,
  type WaSoftAskState,
} from "@/lib/waSoftAsk";
import { analytics } from "@/lib/analytics";

/**
 * useWaSoftAsk — o convite do Espelho para vincular o WhatsApp, no D0.
 *
 * O estado (braço, dono do D0, já vinculado, push resolvido) vem de UMA
 * chamada: a RPC `wa_soft_ask_state()`. Ela existe para o client não precisar
 * abrir `agent_config` (a policy de lá é whitelisted por chave) nem
 * reimplementar o hash — e, principalmente, porque o BRAÇO é lido da linha
 * gravada no cadastro, nunca recalculado. Recalcular na hora de mostrar
 * reembaralharia a coorte a cada mudança de pct.
 *
 * EXPOSIÇÃO É REGISTRADA nos DOIS braços — mas só quando a decisão é FINAL
 * (ver `isWaSoftAskDecisionFinal`). Registrar no primeiro instante em que
 * havia um braço foi o que produziu 255 `shown=false` contra 5 `shown=true`:
 * o evento saía enquanto a resposta ainda podia virar.
 *
 * ── TRÊS DEFEITOS CORRIGIDOS AQUI (11/08) ───────────────────────────────────
 *  1. DEADLOCK DO ÁRBITRO. `state.pushDecided` era lido uma vez na montagem e
 *     nunca mais. No D0 nasce `false` e a única coisa capaz de virá-lo — o
 *     usuário responder ao push — acontece depois, sem releitura. `pushSettled`
 *     entra por parâmetro, ao vivo, e o chip destrava na MESMA sessão.
 *  2. O CHIP PISCAVA. `offeredThisSession` era ligado por um efeito assim que
 *     o chip ficava visível, e realimentava a própria decisão: render 1
 *     mostrava, o efeito marcava "já ofertado", render 2 escondia. Um commit
 *     de vida. Dentro de UMA montagem esse sinal não acrescentava nada a
 *     `dismissed` — é `dismissed` que ele queria dizer, e é o que passa agora.
 *  3. DESFECHO INVISÍVEL. `accepted`/`declined` nunca dispararam nenhuma vez
 *     em produção, e "ignorou" não existia como evento. Agora os três saem
 *     amarrados por `exposure_id`, e `linked` separa tocar de vincular.
 */
// ── Depósito da exposição pendente ──────────────────────────────────────────
// Sobrevive à navegação para o wa.me, que na web destrói o SPA. Guarda o
// MÍNIMO para reconciliar o funil na volta — id da exposição e braço — e nada
// de identificável. Expira: um vínculo que aparece uma semana depois não é
// desfecho deste convite, e creditar seria inflar a métrica primária.
const PENDING_WA_KEY = "vyv_wa_soft_ask_pending";
const PENDING_WA_TTL_MS = 24 * 60 * 60 * 1000;

interface PendingWaExposure { id: string | null; arm: string }

function writePendingWaExposure(p: PendingWaExposure): void {
  try {
    localStorage.setItem(PENDING_WA_KEY, JSON.stringify({ ...p, ts: Date.now() }));
  } catch {
    // Safari privado / storage cheio. Perdemos a confirmação por remontagem,
    // o caminho de foreground continua valendo — e o usuário não perde nada.
  }
}

function readPendingWaExposure(): PendingWaExposure | null {
  try {
    const raw = localStorage.getItem(PENDING_WA_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { id?: unknown; arm?: unknown; ts?: unknown };
    if (typeof p.ts !== "number" || Date.now() - p.ts > PENDING_WA_TTL_MS) {
      clearPendingWaExposure();
      return null;
    }
    return {
      id: typeof p.id === "string" ? p.id : null,
      arm: typeof p.arm === "string" ? p.arm : "unassigned",
    };
  } catch {
    clearPendingWaExposure();
    return null;
  }
}

function clearPendingWaExposure(): void {
  try { localStorage.removeItem(PENDING_WA_KEY); } catch { /* idem */ }
}

// ── Exclusão por DIA, não por montagem (issue #461, P4) ─────────────────────
// O decline sempre prometeu "volta amanhã"; com useState a verdade era "volta
// na próxima montagem": cada refresh reofertava a quem já disse não e cunhava
// exposição nova — denominador inflado. O depósito guarda o DIA (SP) da última
// resposta; no mesmo dia o chip não volta e o gate não re-emite.
const DISMISSED_DAY_KEY = "vyv_wa_soft_ask_dismissed_day";

function readDismissedToday(): boolean {
  try {
    return localStorage.getItem(DISMISSED_DAY_KEY) === waSoftAskDayKey(new Date());
  } catch {
    return false;
  }
}

function writeDismissedToday(): void {
  try {
    localStorage.setItem(DISMISSED_DAY_KEY, waSoftAskDayKey(new Date()));
  } catch {
    // Safari privado / storage cheio: a exclusão degrada para por-montagem,
    // que era o comportamento antigo — nunca pior do que já era.
  }
}

// A leitura do estado tenta 4 vezes (0/1s/3s/9s). O steal-cascade do lock de
// auth (issue #461) mata chamadas da rajada de montagem; segundos depois a
// fila drenou e a MESMA chamada passa. Leitura única transformava o soluço em
// "gate nunca avaliado" — invisível no funil, indistinguível de "nunca abriu
// o /mirror".
const RPC_RETRY_DELAYS_MS = [0, 1000, 3000, 9000];

export interface UseWaSoftAskResult {
  /** Mostrar o chip agora? */
  visible: boolean;
  /** Vincular — abre o WhatsApp. */
  accept: () => Promise<void>;
  /** "Agora não" — some pelo DIA (SP), volta amanhã. */
  decline: () => void;
  activating: boolean;
  /** O último accept falhou — o chip mostra waSoftAsk.error e continua vivo. */
  failed: boolean;
}

export function useWaSoftAsk(
  firstExchangeComplete: boolean,
  /**
   * O push soft-ask já cedeu a vez NESTA sessão — resolvido, adiado, ou
   * incapaz de aparecer (iOS Safari fora de PWA nunca resolve). Vem do
   * componente do push, que é quem sabe.
   */
  pushSettledThisSession = false,
): UseWaSoftAskResult {
  const [state, setState] = useState<WaSoftAskState>(FALLBACK_WA_SOFT_ASK);
  // Nasce do depósito DIÁRIO: quem respondeu hoje não é reofertado por um
  // refresh de página.
  const [dismissed, setDismissed] = useState(() => readDismissedToday());
  const [failed, setFailed] = useState(false);
  const { activate, activating } = useWhatsAppLink();
  /** A atribuição sai UMA vez por montagem, não a cada render. */
  const loggedRef = useRef(false);
  // Se o dia já tinha resposta, o gate DESTE dia já foi emitido na montagem
  // que ofertou — remontagens do mesmo dia não emitem de novo, senão cada
  // refresh de página inflaria o denominador do experimento.
  const alreadyOfferedTodayRef = useRef(dismissed);
  const [stateUnavailable, setStateUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
    void (async () => {
      for (let attempt = 0; attempt < RPC_RETRY_DELAYS_MS.length; attempt++) {
        if (RPC_RETRY_DELAYS_MS[attempt] > 0) await sleep(RPC_RETRY_DELAYS_MS[attempt]);
        if (cancelled) return;
        const { data, error } = await supabase.rpc("wa_soft_ask_state");
        if (cancelled) return;
        if (!error) {
          setState(parseWaSoftAskState(data));
          return;
        }
        // Fail-closed durante o retry: sem estado, não mostra. `warn` nas
        // tentativas intermediárias e `error` só na terminal — 4× error por
        // montagem seria ruído no Sentry, e é a terminal que carrega o fato.
        if (attempt < RPC_RETRY_DELAYS_MS.length - 1) {
          console.warn(
            "[useWaSoftAsk] rpc failed (tentativa " + (attempt + 1) + "/" + RPC_RETRY_DELAYS_MS.length + ")",
            error,
          );
        } else {
          console.error("[useWaSoftAsk] rpc failed — esgotou as tentativas", error);
        }
      }
      setState(FALLBACK_WA_SOFT_ASK);
      setStateUnavailable(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // `offeredThisSession` = `dismissed`, e não um latch próprio. Ver defeito 2
  // no cabeçalho: um sinal separado, ligado no instante em que o chip
  // aparecia, matava o chip no render seguinte.
  const input = {
    state,
    firstExchangeComplete,
    offeredThisSession: dismissed,
    pushSettledThisSession,
  };
  const decision = decideWaSoftAsk(input);
  const visible = decision.show;
  // Primitivos, para as deps do efeito de atribuição não dependerem da
  // identidade de `input`/`decision` (objetos novos a cada render).
  const { arm, assignedPct } = state;
  const isShown = decision.show;

  // ── Identidade da exposição ────────────────────────────────────────────────
  // Cunhada no instante em que o chip aparece e carregada por TODOS os eventos
  // do desfecho. Sem ela, `accepted` e `declined` eram eventos soltos com um
  // `arm`: dava para contar, não para ligar ao que foi exposto — e um funil
  // que não liga exposição a desfecho não distingue "não converteu" de
  // "nunca viu", que é a única pergunta que este experimento faz.
  const exposureIdRef = useRef<string | null>(null);
  if (visible && exposureIdRef.current === null) {
    exposureIdRef.current =
      globalThis.crypto?.randomUUID?.() ?? `wa-${Date.now()}-${performance.now()}`;
  }

  // Espelhos para os handlers que rodam FORA do render (pagehide, unmount,
  // visibilitychange). Ler estado do React nesses caminhos daria closure velha;
  // §11 manda usar ref espelhado para leitura síncrona de estado corrente.
  const armRef = useRef(state.arm);
  armRef.current = state.arm;
  const outcomeRef = useRef(false);
  const exposedRef = useRef(false);
  if (visible) exposedRef.current = true;

  // ATRIBUIÇÃO — um evento nos DOIS braços, com o pct vigente junto. Com pct=0
  // a análise filtra assignedPct > 0 e lê só o período do experimento. Só
  // dispara depois de a RPC responder (arm deixa de ser "unassigned") E de a
  // decisão ser FINAL — senão registra um veredito que ainda pode virar, que é
  // exatamente o que fazia `push_owns_the_moment` parecer desfecho.
  const isFinal = isWaSoftAskDecisionFinal(input, decision);
  const finalReason = decision.show ? null : decision.reason;
  useEffect(() => {
    if (loggedRef.current || alreadyOfferedTodayRef.current) return;
    if (arm === "unassigned" || !isFinal) return;
    loggedRef.current = true;
    analytics.waSoftAskGate({
      arm,
      assignedPct,
      shown: isShown,
      reason: finalReason,
      exposureId: exposureIdRef.current,
    });
  }, [arm, assignedPct, isShown, finalReason, isFinal]);

  // GATE TERMINAL DA INDISPONIBILIDADE (issue #461, P3). Sem isto, a falha da
  // RPC não emitia NADA e as pessoas sumiam do funil — 23 de 292 avaliadas e
  // as outras 269 indistinguíveis de "nunca abriu o /mirror". `state_unavailable`
  // é a fatia que falta no denominador, e é também a régua do P1: se o lock
  // paciente funcionar, esta fatia tem que ser pequena.
  useEffect(() => {
    if (!stateUnavailable || loggedRef.current || alreadyOfferedTodayRef.current) return;
    loggedRef.current = true;
    analytics.waSoftAskGate({
      arm: "unassigned",
      assignedPct: 0,
      shown: false,
      reason: "state_unavailable",
      exposureId: null,
    });
  }, [stateUnavailable]);

  // ── IGNOROU ────────────────────────────────────────────────────────────────
  // O terceiro desfecho, que não existia. Sai quando a exposição termina sem
  // resposta — a pessoa saiu da tela, fechou a aba ou mandou o app para o
  // fundo com o chip na cara. `pagehide` além do unmount porque no mobile a
  // aba morre sem desmontar nada, que é justamente onde está o público.
  useEffect(() => {
    const flush = () => {
      if (!exposedRef.current || outcomeRef.current) return;
      outcomeRef.current = true;
      analytics.waSoftAskIgnored(armRef.current, exposureIdRef.current);
    };
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, []);

  // ── VINCULOU (confirmado) ──────────────────────────────────────────────────
  // `accept` só prova que o botão foi TOCADO. Quem escreve o vínculo é o
  // whatsapp-webhook, depois, SE o usuário realmente enviar a mensagem no
  // WhatsApp — e ele pode abrir o app e não enviar. Tratar toque como conversão
  // inflaria a métrica primária no exato ponto onde ela decide o experimento.
  //
  // A confirmação tem que SOBREVIVER À NAVEGAÇÃO. `activate()` chama
  // `platformOpen(wa.me)`, que na web é navegação de página inteira: o SPA é
  // destruído, e um listener de `visibilitychange` morre junto. Contar só com
  // ele faria `linked` subcontar exatamente na plataforma onde está a maior
  // parte do público — um número que parece medido e não é.
  //
  // Então a exposição é DEPOSITADA antes de sair e reconciliada na volta,
  // venha ela por foreground (nativo, mesma página) ou por remontagem (web,
  // página nova). Sem depender de nada que já não exista: a própria RPC já
  // devolve `alreadyLinked`.
  const acceptedRef = useRef(false);
  const linkedLoggedRef = useRef(false);

  const confirmLink = useCallback((expected: { id: string | null; arm: string }) => {
    if (linkedLoggedRef.current) return;
    void supabase.rpc("wa_soft_ask_state").then(({ data, error }) => {
      if (error) {
        // Falha esperada com fallback real: perdemos a confirmação, o usuário
        // não perde nada. `warn` por §11 critério (b).
        console.warn("[useWaSoftAsk] link confirm recheck failed", error);
        return;
      }
      if (!parseWaSoftAskState(data).alreadyLinked || linkedLoggedRef.current) return;
      linkedLoggedRef.current = true;
      clearPendingWaExposure();
      analytics.waSoftAskLinked(expected.arm, expected.id);
    });
  }, []);

  // Volta por FOREGROUND — nativo, ou aba que sobreviveu.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible" || !acceptedRef.current) return;
      confirmLink({ id: exposureIdRef.current, arm: armRef.current });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [confirmLink]);

  // Volta por REMONTAGEM — web, página nova, refs zerados. A exposição
  // pendente é lida do depósito, então o `exposure_id` do `linked` continua
  // sendo o MESMO do `gate` que o originou: é isso que mantém o funil ligável.
  useEffect(() => {
    const pending = readPendingWaExposure();
    if (!pending) return;
    confirmLink({ id: pending.id, arm: pending.arm });
  }, [confirmLink]);

  const accept = useCallback(async () => {
    setFailed(false);
    // Deposita ANTES de navegar: depois do `platformOpen` pode não haver mais
    // página para executar coisa nenhuma. Se o activate FALHAR, o depósito é
    // limpo no catch — um `linked` posterior não pode creditar um convite que
    // não abriu nada.
    writePendingWaExposure({ id: exposureIdRef.current, arm: armRef.current });
    try {
      await activate("mirror_soft_ask");
    } catch (err) {
      // O defeito antigo (issue #461, P4): dismissed ANTES do await + `void`
      // sem catch = o usuário tocava "Vincular", nada abria, o chip sumia e o
      // funil contava um accept. Agora: chip fica vivo com waSoftAsk.error (a
      // chave que existia sem uso), e NENHUM evento sai — falha não é accept.
      clearPendingWaExposure();
      console.error("[useWaSoftAsk] accept failed", err);
      setFailed(true);
      return;
    }
    // Desfecho SÓ depois de o WhatsApp abrir de fato. Na web o platformOpen já
    // iniciou a navegação e este capture corre contra a morte da página —
    // risco aceito e declarado: a alternativa (contar antes) inflava o funil
    // com falhas, que é pior que subcontar sucesso.
    outcomeRef.current = true;
    acceptedRef.current = true;
    setDismissed(true);
    writeDismissedToday();
    analytics.waSoftAskAccepted(armRef.current, exposureIdRef.current);
  }, [activate]);

  const decline = useCallback(() => {
    outcomeRef.current = true;
    setDismissed(true);
    writeDismissedToday();
    analytics.waSoftAskDeclined(armRef.current, exposureIdRef.current);
  }, []);

  return { visible, accept, decline, activating, failed };
}
